/**
 * MayaExplainsPanel — "Maya Explains" CMS inside the Maya Manager tile.
 * Author each feature's explanation in three fields (What it is · What it
 * does · How to use it). Teens see these via the Maya icon on dashboard
 * features and through Maya's chat answers.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';

const FIELDS = [
  ['what_it_is', 'What it is'],
  ['what_it_does', 'What it does'],
  ['how_to_use', 'How to use it'],
];

const EntryEditor = ({ entry, onSaved, onDeleted }) => {
  const [form, setForm] = useState(entry);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      ['title', 'tags', 'what_it_is', 'what_it_does', 'how_to_use', 'maya_script'].forEach((k) => {
        if ((form[k] ?? '') !== (entry[k] ?? '')) body[k] = form[k] ?? '';
      });
      if (Object.keys(body).length === 0) { toast.info('No changes to save'); setSaving(false); return; }
      const updated = await apiClient.request(`/api/maya-manager/guide/${entry.key}`, { method: 'PATCH', body: JSON.stringify(body) });
      toast.success('Saved');
      onSaved(updated);
    } catch (e) { toast.error(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${entry.title}"?`)) return;
    try {
      await apiClient.request(`/api/maya-manager/guide/${entry.key}`, { method: 'DELETE' });
      toast.success('Deleted');
      onDeleted(entry.key);
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
  };

  return (
    <div className="mt-3 space-y-3" data-testid={`explains-editor-${entry.key}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Title</span>
          <input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" data-testid={`explains-title-${entry.key}`} />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Match keywords (tags)</span>
          <input value={form.tags || ''} onChange={(e) => setForm({ ...form, tags: e.target.value })}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="vault trophies nft rewards" />
        </label>
      </div>
      {FIELDS.map(([k, label]) => (
        <label key={k} className="block">
          <span className="text-[11px] font-bold text-orange-500 uppercase tracking-wide">{label}</span>
          <textarea value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={2}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" data-testid={`explains-${k}-${entry.key}`} />
        </label>
      ))}
      <label className="block">
        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Maya&apos;s chat voice-over (fallback paragraph)</span>
        <textarea value={form.maya_script || ''} onChange={(e) => setForm({ ...form, maya_script: e.target.value })} rows={2}
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
      </label>
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving}
          className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-60"
          data-testid={`explains-save-${entry.key}`}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={remove} className="p-2 rounded-lg text-red-500 hover:bg-red-50" aria-label="Delete entry"
          data-testid={`explains-delete-${entry.key}`}>
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
};

const MayaExplainsPanel = () => {
  const [entries, setEntries] = useState(null);
  const [q, setQ] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const load = () => apiClient.request('/api/maya-manager/guide')
    .then((d) => setEntries(d?.entries || []))
    .catch(() => setEntries([]));
  useEffect(() => { load(); }, []);

  const toggleEnabled = async (e) => {
    try {
      const updated = await apiClient.request(`/api/maya-manager/guide/${e.key}`, {
        method: 'PATCH', body: JSON.stringify({ enabled: !(e.enabled !== false) }),
      });
      setEntries((rows) => rows.map((r) => (r.key === e.key ? updated : r)));
    } catch (err) { toast.error('Update failed'); }
  };

  const createEntry = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const doc = await apiClient.request('/api/maya-manager/guide', {
        method: 'POST', body: JSON.stringify({ title: newTitle.trim() }),
      });
      setEntries((rows) => [doc, ...(rows || [])]);
      setNewTitle('');
      setOpenKey(doc.key);
      toast.success('Entry created — fill in the three fields');
    } catch (e) { toast.error(e?.message || 'Create failed'); }
    finally { setCreating(false); }
  };

  if (entries === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-orange-400" size={22} /></div>;

  const filtered = entries.filter((e) => !q || `${e.title} ${e.tags}`.toLowerCase().includes(q.toLowerCase()));
  const structured = entries.filter((e) => e.what_it_is || e.how_to_use).length;

  return (
    <div data-testid="maya-explains-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entries…"
            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm" data-testid="explains-search" />
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap" data-testid="explains-count">
          {entries.length} entries · {structured} fully structured
        </span>
      </div>

      <div className="flex gap-2 mb-5">
        <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New feature title…"
          onKeyDown={(e) => e.key === 'Enter' && createEntry()}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm" data-testid="explains-new-title" />
        <button onClick={createEntry} disabled={creating || !newTitle.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold disabled:opacity-50"
          data-testid="explains-create-btn">
          <Plus size={14} /> Add
        </button>
      </div>

      <ul className="space-y-2">
        {filtered.map((e) => (
          <li key={e.key} className="rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid={`explains-row-${e.key}`}>
            <div className="flex items-center gap-3">
              <button onClick={() => setOpenKey(openKey === e.key ? null : e.key)}
                className="flex-1 min-w-0 flex items-center gap-2 text-left" data-testid={`explains-toggle-${e.key}`}>
                {openKey === e.key ? <ChevronUp size={14} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />}
                <span className="font-semibold text-sm text-gray-900 truncate">{e.title}</span>
                {(e.what_it_is || e.how_to_use)
                  ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold flex-shrink-0">structured</span>
                  : <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex-shrink-0">{e.source || 'legacy'}</span>}
              </button>
              <button onClick={() => toggleEnabled(e)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold border flex-shrink-0 ${e.enabled !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}
                data-testid={`explains-enabled-${e.key}`}>
                {e.enabled !== false ? 'LIVE' : 'OFF'}
              </button>
            </div>
            {openKey === e.key && (
              <EntryEditor
                entry={e}
                onSaved={(u) => setEntries((rows) => rows.map((r) => (r.key === u.key ? u : r)))}
                onDeleted={(key) => { setEntries((rows) => rows.filter((r) => r.key !== key)); setOpenKey(null); }}
              />
            )}
          </li>
        ))}
        {filtered.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">No entries match.</p>}
      </ul>
    </div>
  );
};

export default MayaExplainsPanel;
